import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppState,
  Image,
  Platform,
  StatusBar,
  StyleSheet,
  useWindowDimensions,
  View,
  Text,
} from 'react-native';
import { COLORS, applyTransparency } from '../../../constants/colors';
import { useGraphQL } from '../../../graphql';
import { ScreenProps } from '../../../typesInterfacesEnums/componentProps';
import { logAnalytics } from '../../../utils/analytics';
import { SCREEN_SIZES, useScreenSize } from '../../../utils/dimsHooks';
import { useOpenClose } from '../../../utils/hooks';
import { ComponentWrapper } from '../../helperComponents/ComponentWrapper';
import { Controls } from '../../helperComponents/Controls';
import { Divider } from '../../helperComponents/Divider';
import { ExitPopup } from '../../helperComponents/ExitPopup';
import { ResultPopup } from '../../helperComponents/ResultPopup';
import { Turn } from '../../helperComponents/Turn';
import WinningBee from '../../helperComponents/WinningBee';
import { GameStatus } from '../PlayOnline/useGameEventSubscription.gql';
import { TilesView } from './TilesView';
import { useChangeTurnMutation } from './useChangeTurnMutation.gql';
import { useClaimGameTileMutation } from './useClaimTileMutation.gql';
import {
  EventTypeGameEventType,
  GameMode,
  GameType,
  useGameEventSubscription,
} from './useGameEventSubscription.gql';
import { useLeaveGameMutation } from './useLeaveGameMutation.gql';
import { getDefaultOperands, performOperation } from './utils';
import { useGetLatestUpdate } from './useGetLatestUpdate.gql';
import { HintPopup } from '../../helperComponents/HintPopup';
import { WorkingBar } from '../../helperComponents/WorkingBar';
import { ReconnectPopup } from '../../helperComponents/ReconnectPopup';
import { useSignedInContext } from '../../../context/SignedInContext';
import { GAME_TYPES_CONFIG, OPERAND_TYPE } from '../../../constants/constants';
import isEqual from 'lodash/isEqual';
import { isMobile } from '@mightybyte/rnw.utils.device-info';
import { useGetCurrentUserData } from '../../../hooks/user';
import cloneDeep from 'lodash/cloneDeep';
import { buildOperandString, addCAndYIfUndefined } from '../../helperComponents/OldGameCYLegacy';
import { getBeeBotByDifficulty, getMascot } from '../../../utils/journeysUtils';
import { AIDifficultyLevel } from '../PlayWithAI/useJoinAiGameMutation.gql';
import JourneyResultPopup from '../../helperComponents/Journey/JourneyResultPopup';
import { useIncrementStreakOnGamePlayMutation } from '../../../graphql/useIncrementStreakOnGamePlayMutation.gql';
import { useGetHivesQuery } from '../MyHives/useGetHivesQuery.gql';
import { useIncrementGymAIPracticeGamesPlayedMutation } from '../Gym/Stats/useIncrementGymAIPracticeGamesPlayedMutation.gql';
import { useIncrementGymAIPracticeGamesWonMutation } from '../Gym/Stats/useIcrementGymAIPracticeGamesWon.gql';
import { useIncrementGymPlayFriendGamesPlayedMutation } from '../Gym/Stats/useIncrementGymPlayFriendGamesPlayedMutation.gql';
import { useIncrementGymPlayFriendGamesWonMutation } from '../Gym/Stats/useIcrementGymPlayFriendGamesWon.gql';

const Game = ({ navigation, route }: ScreenProps<'Game'>) => {
  const { user: currentUser } = useSignedInContext();
  const screenSize = useScreenSize();
  const [game, setGame] = useState(route.params.game);
  const gameMode = useRef(route.params.mode);
  const playFriendMode =
    gameMode.current === GameMode.HOST_A_GAME || gameMode.current === GameMode.JOIN_A_GAME;

  const gameTiles = game.gameState.tiles;
  const turn = game.gameState.turn;
  const userNameTurn = game.gameState.userNameTurn;

  const journeyMode = route.params.journey;

  const journeyDifficulty = useRef<AIDifficultyLevel | undefined>(route.params.difficulty);
  // for joureny boss
  const bossUnlocked = route.params.bossUnlocked;

  const gameType = game.gameState.gameType;
  const journeyId = route.params.journeyId;

  const players = game.users;
  const gameLeftOperand = game.gameState.leftOperand ?? getDefaultOperands(gameType).leftOperand;
  const gameRightOperand = game.gameState.rightOperand ?? getDefaultOperands(gameType).rightOperand;

  const { currentUserId: userId, isOnline } = useGraphQL();
  const { refetchCurrentUserData } = useGetCurrentUserData();
  const [leaveGame] = useLeaveGameMutation();
  const [claimGameTile] = useClaimGameTileMutation();
  const [changeTurn] = useChangeTurnMutation();
  const [getLatestUpdate, { data: latestGameData }] = useGetLatestUpdate({
    variables: { gameId: game.id },
  });
  const { data: gameEventSubscription } = useGameEventSubscription();

  const currentUserId = currentUser ? currentUser.id : userId;

  const [firstSlider, setFirstSlider] = useState(gameLeftOperand);
  const [secondSlider, setSecondSlider] = useState(gameRightOperand);
  const [timer, setTimer] = useState(60 * 1000);

  const [isResultPopupOpen, setResultPopupOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isCurrentPlayerWinner, setCurrentPlayerWinner] = useState(false);
  const [isGameDraw, setGameDraw] = useState(false);
  const [isExitPopupVisible, setExitPopupOpen, setExitPopupClose] = useOpenClose();
  const [isHintPopupVisible, setHintPopupOpen, setHintPopupClose] = useOpenClose();
  const [incrementStreak] = useIncrementStreakOnGamePlayMutation();

  const [incrementGymAIPracticeGamesPlayed] = useIncrementGymAIPracticeGamesPlayedMutation();
  const [incrementGymAIPracticeGamesWon] = useIncrementGymAIPracticeGamesWonMutation();
  const [incrementGymPlayFriendGamesPlayed] = useIncrementGymPlayFriendGamesPlayedMutation();
  const [incrementGymPlayFriendGamesWon] = useIncrementGymPlayFriendGamesWonMutation();

  const [changedSliderIndex, setChangedSliderIndex] = useState<null | number>(null);

  const isPassAndPlay = Boolean(game.gameState.userNameTurn);

  const currentPlayerIndex = isPassAndPlay
    ? 0
    : players.findIndex(user => user.id === currentUserId);
  const otherPlayerIndex = currentPlayerIndex === 0 ? 1 : 0;
  const anotherPlayer = isPassAndPlay
    ? players[otherPlayerIndex]
    : players.find(user => user.id !== currentUserId);

  const [winningTiles, setWinningTiles] = useState<number[] | undefined>(undefined);
  const [winningDirection, setWinningDirection] = useState<number[] | undefined>(undefined);

  const [hasPlayedOnce, setHasPlayedOnce] = useState<boolean>(false);
  const remainedTime =
    Math.ceil(timer / 1000) - Math.ceil(new Date(game.gameState.lastPlayedTimeAt).getTime() / 1000);

  const isLarge = screenSize === SCREEN_SIZES.LARGE;
  const isSmall = screenSize === SCREEN_SIZES.SMALL;

  const { width } = useWindowDimensions();

  const beeLimit = width < 910;

  const [waitingTiles, setWaitingTiles] = useState<number[]>([]);

  const requestRef = useRef<any>();
  const previousTimeRef = useRef<any>();
  const animate = useCallback((time: number) => {
    if (previousTimeRef.current !== undefined) {
      setTimer(new Date().getTime());
    }
    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  }, []);

  const onHideResultPopUp = useCallback(
    (value: boolean) => {
      if (!value) {
        refetchCurrentUserData();
      }
      setResultPopupOpen(value);
    },
    [refetchCurrentUserData],
  );

  const restartTimer = () => {
    setTimer(new Date().getTime());
  };

  useEffect(() => {
    if (currentUser) {
      const run = async () => {
        try {
          await incrementStreak({
            variables: { userId: currentUser.id },
          });
        } catch (err) {
          console.error('Failed to increment streak', err);
        }
      };

      run();
    }
  }, [currentUser, incrementStreak]);

  const handlePlayAgainPress = () => {
    refetchCurrentUserData();
    setExitPopupClose();
    setResultPopupOpen(false);
    setTimeout(() => {
      if (gameMode.current === GameMode.PRACTICE) {
        navigation.reset({
          index: 1,
          routes: [
            { name: 'Lobby' },
            {
              name: 'PlayWithAI',
              params: {
                userName: players[currentPlayerIndex].name,
                difficulty: route.params.difficulty,
                isPlayAgain: true,
                gameType,
                journeyId,
              },
            },
          ],
        });
      } else {
        navigation.reset({
          index: 1,
          routes: [
            { name: 'Lobby' },
            {
              name: 'PlayOnline',
              params: {
                userName: players[currentPlayerIndex].name,
                isPlayAgain: true,
                gameType,
              },
            },
          ],
        });
      }
    }, 500);
  };

  const handleBackToLobbyPress = useCallback(() => {
    leaveGame({ variables: { gameId: game.id } }).then(() => {
      refetchCurrentUserData();
    });
    setExitPopupClose();
    setResultPopupOpen(false);
    setTimeout(() => {
      if (!journeyMode) {
        navigation.reset({
          index: 0,
          routes: [{ name: 'Lobby' }],
        });
      }

      if (journeyMode) {
        navigation.reset({
          index: 0,
          routes: [
            {
              name: 'LobbyJourney',
              params: {
                gameType: gameType,
                journey: true,
              },
            },
          ],
        });
      }
    }, 500);
  }, [game.id, leaveGame, navigation, refetchCurrentUserData, setExitPopupClose]);

  const handleClaimTile = useCallback(
    (leftOperand: OPERAND_TYPE, rightOperand: OPERAND_TYPE, tileId?: number) => {
      const currentGame = cloneDeep(game);

      setGame(oldGame => {
        const updatedGame = cloneDeep(oldGame);
        const claimedTileValue = performOperation({ leftOperand, rightOperand, gameType });
        updatedGame.gameState.leftOperand = leftOperand;
        updatedGame.gameState.rightOperand = rightOperand;
        updatedGame.gameState.turn = anotherPlayer?.id ?? updatedGame.gameState.turn;
        updatedGame.gameState.tiles = updatedGame.gameState.tiles.map(tile =>
          (
            tileId
              ? tile.id === tileId
              : isEqual(claimedTileValue, tile.value) &&
                (isPassAndPlay ? true : tile.claimedBy !== anotherPlayer?.id)
          )
            ? {
                ...tile,
                claimedBy: currentUserId,
                claimedByUserName: isPassAndPlay ? updatedGame.gameState.userNameTurn : undefined,
              }
            : tile,
        );
        return updatedGame;
      });

      setWaitingTiles([]);
      claimGameTile({
        variables: {
          leftOperand: buildOperandString(leftOperand),
          rightOperand: buildOperandString(rightOperand),
          gameId: game.id,
          tileId: tileId ?? undefined,
        },
      })
        .then(({ data }) => data && setGame(data.claimGameTileV2))
        .catch(error => {
          if (error.message === 'Game not in progress.') {
            setIsError(true);
            setResultPopupOpen(true);
          } else {
            console.error('Error occurred while claim game tile', currentGame);
            setGame(currentGame);
            setFirstSlider(currentGame.gameState.leftOperand);
            setSecondSlider(currentGame.gameState.rightOperand);
            getLatestUpdate();
          }
        });
    },
    [
      anotherPlayer?.id,
      claimGameTile,
      currentUserId,
      game,
      gameType,
      getLatestUpdate,
      isPassAndPlay,
    ],
  );

  const handleTilePress = (tileId: number) => {
    if (waitingTiles.length > 1) {
      console.log('waiting tiles');
      handleClaimTile(firstSlider, secondSlider, tileId);
    }
  };

  const handleSliderChange = useCallback(
    (sliderIdx: number | null, leftOperand: OPERAND_TYPE, rightOperand: OPERAND_TYPE) => {
      setHasPlayedOnce(true);
      setChangedSliderIndex(sliderIdx);
      sliderIdx === 0 ? setFirstSlider(leftOperand) : setSecondSlider(rightOperand);
      if (GAME_TYPES_CONFIG[gameType].hasDuplicate) {
        const claimedTileValue = addCAndYIfUndefined(
          performOperation({
            leftOperand,
            rightOperand,
            gameType,
          }),
        );

        let waitingToSelectTiles: number[] = [];
        for (const tile of gameTiles) {
          if (isEqual(tile.value, claimedTileValue) && tile.claimedBy === '-1') {
            waitingToSelectTiles.push(tile.id);
          }
        }
        if (waitingToSelectTiles.length > 1) {
          setWaitingTiles(waitingToSelectTiles);
        } else {
          if (waitingToSelectTiles.length === 0) {
            handleClaimTile(leftOperand, rightOperand, undefined);
          }
          handleClaimTile(leftOperand, rightOperand, waitingToSelectTiles[0]);
        }
      } else {
        handleClaimTile(leftOperand, rightOperand);
      }
    },
    [gameType, gameTiles, handleClaimTile],
  );

  useEffect(() => {
    if (gameEventSubscription && gameEventSubscription.gameEvent.game.id !== game.id) {
      console.warn(
        'ignore events from another game',
        gameEventSubscription?.gameEvent.game.id,
        game.id,
      );
      return;
    }

    const showUpdate = (gameUpdate: GameType) => {
      if (gameUpdate.id === game.id) {
        if (gameUpdate.status === GameStatus.FINISHED) {
          if (gameUpdate.gameState.winningPlayerId === currentUserId) {
            setCurrentPlayerWinner(true);
            setResultPopupOpen(true);
            // ✅ Increment Gym AI practice WON counter (only if practice, not journey)
            if (gameMode.current === GameMode.PRACTICE && !journeyMode && currentUser) {
              incrementGymAIPracticeGamesWon({
                variables: {
                  input: {
                    userId: currentUser.id,
                    gameType,
                    aiDifficultyLevel: route.params.difficulty as AIDifficultyLevel,
                  },
                },
              }).catch((err: any) => {
                console.error('❌ Failed to increment Gym AI practice games won', err);
              });
            }

            // ✅ Increment Gym play friend WON counter (only if playFriend, not journey)
            if (playFriendMode && !journeyMode && currentUser) {
              incrementGymPlayFriendGamesWon({
                variables: {
                  input: {
                    userId: currentUser.id,
                    gameType,
                    opponentName: players[otherPlayerIndex].name,
                  },
                },
              }).catch((err: any) => {
                console.error('❌ Failed to increment Gym play friend games won', err);
              });
            }
          } else if (gameUpdate.gameState.winningPlayerId === '-1') {
            setGameDraw(true);
            setResultPopupOpen(true);
          } else {
            setCurrentPlayerWinner(false);
            setResultPopupOpen(true);
          }
        }
      }
    };

    if (gameEventSubscription?.gameEvent.eventType === EventTypeGameEventType.ON_CLAIM_TILE) {
      const gameUpdate = gameEventSubscription?.gameEvent.game;
      if (gameEventSubscription.gameEvent.game.gameState.winningDirection.length > 0) {
        setWinningDirection(gameEventSubscription.gameEvent.game.gameState.winningDirection);
      }
      if (gameEventSubscription.gameEvent.game.gameState.winningTiles.length > 0) {
        setWinningTiles(gameEventSubscription.gameEvent.game.gameState.winningTiles);
      }
      setGame(gameEventSubscription.gameEvent.game);
      setFirstSlider(gameUpdate.gameState.leftOperand);
      setSecondSlider(gameUpdate.gameState.rightOperand);
      setTimeout(() => {
        showUpdate(gameUpdate);
      }, 1500);
      restartTimer();
    }
    if (gameEventSubscription?.gameEvent.eventType === EventTypeGameEventType.ON_CHANGE_TURN) {
      const gameUpdate = gameEventSubscription?.gameEvent.game;
      setGame(gameEventSubscription.gameEvent.game);
      showUpdate(gameUpdate);
    }
    if (gameEventSubscription?.gameEvent.eventType === EventTypeGameEventType.ON_USER_LEFT) {
      showUpdate(gameEventSubscription?.gameEvent.game);
    }
  }, [currentUserId, game.id, gameEventSubscription, navigation]);

  useEffect(() => {
    if (currentUserId) {
      if (turn === currentUserId) {
        setChangedSliderIndex(null);
      }
    }
  }, [turn, currentUserId]);

  // increment played counter for Gym AI practice mode (not journey)
  useEffect(() => {
    // increment played counter for Gym AI practice mode (not journey)
    if (
      game.id &&
      gameMode.current === GameMode.PRACTICE && // must be practice
      !journeyMode && // not journey
      currentUser
    ) {
      incrementGymAIPracticeGamesPlayed({
        variables: {
          input: {
            userId: currentUser.id,
            gameType,
            aiDifficultyLevel: route.params.difficulty as AIDifficultyLevel,
          },
        },
      }).catch((err: any) => {
        console.error('❌ Failed to increment Gym AI practice games played', err);
      });
    }

    // increment played counter for Gym play friend mode (not journey)
    if (
      game.id &&
      playFriendMode && // must be practice
      !journeyMode && // not journey
      currentUser
    ) {
      incrementGymPlayFriendGamesPlayed({
        variables: {
          input: {
            userId: currentUser.id,
            gameType,
            opponentName: players[otherPlayerIndex].name,
          },
        },
      }).catch((err: any) => {
        console.error('❌ Failed to increment Gym play friend games played', err);
      });
    }
  }, [game.id]);

  const handleTimerEnds = useMemo(
    () => () => {
      if (anotherPlayer?.id && game.status !== GameStatus.FINISHED) {
        changeTurn({
          variables: {
            turn: turn === currentUserId ? anotherPlayer.id : currentUserId,
            gameId: game.id,
          },
        });
      }
    },
    [anotherPlayer?.id, changeTurn, currentUserId, game.id, game.status, turn],
  );

  useEffect(() => {
    if (remainedTime > 59 && gameMode.current === GameMode.PLAY_ONLINE) {
      cancelAnimationFrame(requestRef.current);
      setTimeout(() => {
        handleTimerEnds();
      }, 1500);
    }
  }, [handleTimerEnds, remainedTime, timer]);

  useEffect(() => {
    if (gameMode.current === GameMode.PLAY_ONLINE) {
      restartTimer();
      requestRef.current = requestAnimationFrame(animate);
    }
    return () => cancelAnimationFrame(requestRef.current);
  }, [animate, turn]);

  useEffect(() => {
    if (game.id) {
      logAnalytics('game_started', {
        gameId: game.id,
        platform: Platform.OS,
        mode: gameMode.current,
      });
    }
  }, [game.id]);

  useEffect(() => {
    const subscription: any = AppState.addEventListener('change', nextAppState => {
      if (nextAppState === 'active') {
        getLatestUpdate();
      }
    });

    return () => {
      subscription.remove();
    };
  }, [game.id, getLatestUpdate]);

  useEffect(() => {
    if (isOnline) {
      setTimeout(() => {
        getLatestUpdate();
      }, 1000);
    }
  }, [getLatestUpdate, isOnline]);

  useEffect(() => {
    if (latestGameData?.getLatestUpdate && currentUserId) {
      setGame(latestGameData.getLatestUpdate.game);
      setFirstSlider(latestGameData.getLatestUpdate.game.gameState.leftOperand);
      setSecondSlider(latestGameData.getLatestUpdate.game.gameState.rightOperand);
      if (latestGameData?.getLatestUpdate.game.status === GameStatus.FINISHED) {
        cancelAnimationFrame(requestRef.current);
        if (latestGameData?.getLatestUpdate.game.gameState.winningPlayerId === currentUserId) {
          setCurrentPlayerWinner(true);
          setResultPopupOpen(true);
        } else if (latestGameData?.getLatestUpdate.game.gameState.winningPlayerId === '-1') {
          setGameDraw(true);
          setResultPopupOpen(true);
        } else {
          setCurrentPlayerWinner(false);
          setResultPopupOpen(true);
        }
      }
    }
  }, [currentUserId, latestGameData]);

  useEffect(() => {
    if (currentPlayerIndex === -1) {
      navigation.replace('Lobby');
    }
  }, [currentPlayerIndex, navigation]);

  if (currentPlayerIndex === -1) {
    return <></>;
  }

  const renderJourneyAIBee = (bossUnlocked?: boolean) => {
    if (beeLimit) {
      return;
    }
    return (
      <Image
        resizeMode="cover"
        style={[styles.beeAI]}
        source={getMascot({
          difficultyLevel: journeyDifficulty.current || ('EASY' as AIDifficultyLevel),
          bossUnlocked,
        })}
      />
    );
  };

  const beeBot = useMemo(
    () => getBeeBotByDifficulty((journeyDifficulty.current || 'EASY') as AIDifficultyLevel),
    [journeyDifficulty],
  );

  return (
    <>
      <StatusBar backgroundColor={COLORS.white} />
      <ComponentWrapper
        backgroundColor="#FCEABB8C"
        hasBackground={false}
        hasInset
        scrollEnabled={false}
        style={{ paddingHorizontal: 0 }}
        hideBee={true}
      >
        <View style={styles.topContainer}>
          <Turn
            players={players}
            turn={turn}
            isPassAndPlay={isPassAndPlay}
            userNameTurn={userNameTurn}
            currentUserId={currentUserId}
            currentPlayerUserName={players[currentPlayerIndex].name}
            currentPlayerIndex={currentPlayerIndex}
            otherPlayerIndex={otherPlayerIndex}
            setExitPopupOpen={setExitPopupOpen}
            setHintPopupOpen={setHintPopupOpen}
            time={remainedTime < 0 ? 60 : remainedTime > 60 ? 60 : 60 - remainedTime}
            hideTimer={gameMode.current !== GameMode.PLAY_ONLINE}
            isPractice={gameMode.current === GameMode.PRACTICE}
            gameType={gameType}
          />
          <Divider empty height={isLarge ? 20 : 8} />
          <View style={{ width: '100%', justifyContent: 'center', alignItems: 'center' }}>
            <View style={{ position: 'relative', marginTop: !isSmall && !isMobile ? -30 : 0 }}>
              <TilesView
                onPress={handleTilePress}
                waitingTiles={waitingTiles}
                gameType={gameType}
                gameTiles={gameTiles}
                currentUserId={currentUserId}
                isPassAndPlay={isPassAndPlay}
                currentUserName={players[currentPlayerIndex].name}
              />
              <WinningBee
                gameType={gameType}
                winningDirection={winningDirection}
                winningTiles={winningTiles}
              />
            </View>
          </View>
        </View>
        <View style={[styles.beeAIContainer]}>
          {journeyMode && !beeLimit && (
            <>
              <View style={styles.AINameContainer}>
                {bossUnlocked ? (
                  <Text style={styles.textAIName}>Bee-{beeBot}</Text>
                ) : (
                  <Text style={styles.textAIName}>Team {beeBot}</Text>
                )}
              </View>
              {renderJourneyAIBee(bossUnlocked)}
            </>
          )}
        </View>
        <Controls
          turn={turn}
          currentPlayerIndex={currentPlayerIndex}
          currentUserId={currentUserId}
          secondSlider={secondSlider}
          firstSlider={firstSlider}
          handleSliderChange={handleSliderChange}
          setExitPopupOpen={setExitPopupOpen}
          changedSliderIndex={changedSliderIndex}
          time={remainedTime < 0 ? 60 : remainedTime > 60 ? 60 : 60 - remainedTime}
          showHands={!hasPlayedOnce && turn === currentUserId}
          hideTimer={gameMode.current !== GameMode.PLAY_ONLINE}
          gameType={gameType}
          isWaitingTiles={waitingTiles.length > 0}
        />

        <Divider empty height={16} />
        {GAME_TYPES_CONFIG[gameType].hasWorkingBar && <WorkingBar gameType={gameType} />}
        {journeyMode ? (
          <JourneyResultPopup
            visible={isResultPopupOpen}
            setVisible={onHideResultPopUp}
            isWinner={isCurrentPlayerWinner}
            isDraw={isGameDraw}
            isError={isError}
            beeBot={beeBot}
            gameType={gameType}
            navigation={navigation}
            route={route}
          />
        ) : (
          <ResultPopup
            isWinner={isCurrentPlayerWinner}
            isDraw={isGameDraw}
            isError={isError}
            isPassAndPlay={isPassAndPlay}
            winnerName={game.gameState.winningPlayerUserName}
            onPressAction={handleBackToLobbyPress}
            visible={isResultPopupOpen}
            setVisible={onHideResultPopUp}
            anotherPlayerLeft={
              gameEventSubscription?.gameEvent.eventType === EventTypeGameEventType.ON_USER_LEFT &&
              !isError
            }
            handlePlayAgainPress={
              (gameMode.current === GameMode.PLAY_ONLINE ||
                gameMode.current === GameMode.PRACTICE) &&
              !isError
                ? handlePlayAgainPress
                : undefined
            }
            points={
              gameEventSubscription?.gameEvent?.earnedPoints
                ? Number(gameEventSubscription?.gameEvent?.earnedPoints)
                : undefined
            }
          />
        )}
        <ExitPopup
          onYesPress={handleBackToLobbyPress}
          onNoPress={setExitPopupClose}
          visible={isExitPopupVisible}
          setVisible={setExitPopupOpen}
        />
        <ReconnectPopup visible={!isOnline} setVisible={() => {}} />
        {gameMode.current === GameMode.PRACTICE ? (
          <HintPopup gameId={game.id} visible={isHintPopupVisible} setVisible={setHintPopupClose} />
        ) : null}
      </ComponentWrapper>
    </>
  );
};

export { Game };

const styles = StyleSheet.create({
  topContainer: {
    paddingHorizontal: 12,
    position: 'relative',
    width: '100%',
    flexDirection: 'column',
  },
  gameInfo: {
    height: 36,
    width: 148,
    borderColor: applyTransparency('#FFFFFF', 0.4),
    borderWidth: 3,
    borderRadius: 5,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  infoText: {
    fontSize: 12,
    fontFamily: 'Secular One',
    color: 'white',
  },
  infoContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  exitButton: { borderWidth: 3 },
  exitIcon: { marginTop: 4, marginRight: 2 },
  AINameContainer: {
    width: 150,
    height: 57,
    backgroundColor: '#FEDD88',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: -40,
  },
  textAIName: {
    fontFamily: 'Secular One',
    fontSize: 14,
    fontWeight: '400',
    color: '#121212',
  },
  beeAIContainer: {
    position: 'absolute',
    top: '50%',
    right: '5%',
  },
  beeAI: {
    width: 225,
    height: 263,
  },
});
